import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ListGroup, ListGroupItem, Input } from "components/bootstrap";
import { PaginatedList, SearchForm, ModalSubmit, Spinner, NoSearchResult } from "components/common";
import useDashboards from "views/components/dashboard/hooks/useDashboards";
const CopyToDashboardForm = ({
  onCancel,
  onCopyToDashboard,
  submitButtonText,
  submitLoadingText,
  activeDashboardId = void 0,
  onCreateNewDashboard = void 0
}) => {
  const { t } = useTranslation("widgets");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const isMounted = useRef();
  const [createNewDashboard, setCreateNewDashboard] = useState(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const [searchParams, setSearchParams] = useState({
    page: 1,
    pageSize: 5,
    query: "",
    sort: {
      attributeId: "title",
      direction: "asc"
    }
  });
  const { data: paginatedDashboards, isInitialLoading: isLoadingDashboards } = useDashboards({
    ...searchParams,
    scope: "update"
  });
  useEffect(() => {
    setSelectedDashboard(null);
  }, [searchParams]);
  const handleSearch = useCallback(
    (newQuery) => setSearchParams((cur) => ({ ...cur, query: newQuery, page: 1 })),
    []
  );
  const handleSearchReset = useCallback(() => handleSearch(""), [handleSearch]);
  const handlePageChange = useCallback(
    (newPage, newPageSize) => setSearchParams((cur) => ({ ...cur, page: newPage, pageSize: newPageSize })),
    []
  );
  const handleSubmit = () => {
    setIsSubmitting(true);
    const submitHandler = async () => createNewDashboard ? onCreateNewDashboard() : onCopyToDashboard(selectedDashboard);
    submitHandler().then(() => {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    });
  };
  const onSelectDashboard = (dashboardId) => {
    setSelectedDashboard(dashboardId);
    if (createNewDashboard) {
      setCreateNewDashboard(false);
    }
  };
  const toggleCreateNewDashboard = () => {
    setCreateNewDashboard((cur) => !cur);
    if (selectedDashboard) {
      setSelectedDashboard(null);
    }
  };
  const showCreateNewDashboardCheckbox = typeof onCreateNewDashboard === "function";
  return /* @__PURE__ */ React.createElement(Modal, { show: true, onHide: onCancel }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, t("copy_to_dashboard.title"))), /* @__PURE__ */ React.createElement(Modal.Body, null, isLoadingDashboards && /* @__PURE__ */ React.createElement(Spinner, null), !isLoadingDashboards && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      onChange: handlePageChange,
      activePage: searchParams.page,
      totalItems: paginatedDashboards.pagination.total,
      pageSize: searchParams.pageSize,
      pageSizes: [5, 10, 15],
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement("div", { style: { marginBottom: "5px" } }, /* @__PURE__ */ React.createElement(SearchForm, { onSearch: handleSearch, onReset: handleSearchReset })),
    paginatedDashboards.list.length ? /* @__PURE__ */ React.createElement(ListGroup, null, paginatedDashboards.list.map((dashboard) => {
      const isActiveDashboard = activeDashboardId === dashboard.id;
      return /* @__PURE__ */ React.createElement(
        ListGroupItem,
        {
          active: selectedDashboard === dashboard.id,
          onClick: isActiveDashboard ? void 0 : () => onSelectDashboard(dashboard.id),
          header: dashboard.title,
          disabled: isActiveDashboard,
          key: dashboard.id
        },
        dashboard.summary
      );
    })) : /* @__PURE__ */ React.createElement(NoSearchResult, null, t("copy_to_dashboard.no_dashboards"))
  ), showCreateNewDashboardCheckbox && /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      id: "create-new-dashboard",
      name: "createNewDashboard",
      label: t("copy_to_dashboard.create_new"),
      onChange: toggleCreateNewDashboard,
      checked: createNewDashboard
    }
  ))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      submitButtonText,
      submitLoadingText,
      isAsyncSubmit: true,
      isSubmitting,
      disabledSubmit: !selectedDashboard && !createNewDashboard,
      submitButtonType: "button",
      onSubmit: handleSubmit,
      onCancel
    }
  )));
};
export default CopyToDashboardForm;
